var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) =>
  key in obj
    ? __defProp(obj, key, {
        enumerable: true,
        configurable: true,
        writable: true,
        value,
      })
    : (obj[key] = value);
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/index.tsx
import React2 from "react";
import { z } from "zod";

// src/EmailMarkdown.tsx
import insane from "insane";
import { marked, Renderer } from "marked";
import React, { useMemo } from "react";
var ALLOWED_TAGS = [
  "a",
  "article",
  "b",
  "blockquote",
  "br",
  "caption",
  "code",
  "del",
  "details",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "ins",
  "kbd",
  "li",
  "main",
  "ol",
  "p",
  "pre",
  "section",
  "span",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];
var GENERIC_ALLOWED_ATTRIBUTES = ["style", "title"];
function sanitizer(html) {
  return insane(html, {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: __spreadProps(
      __spreadValues(
        {},
        ALLOWED_TAGS.reduce((res, tag) => {
          res[tag] = [...GENERIC_ALLOWED_ATTRIBUTES];
          return res;
        }, {})
      ),
      {
        img: [
          "src",
          "srcset",
          "alt",
          "width",
          "height",
          ...GENERIC_ALLOWED_ATTRIBUTES,
        ],
        table: ["width", ...GENERIC_ALLOWED_ATTRIBUTES],
        td: ["align", "width", ...GENERIC_ALLOWED_ATTRIBUTES],
        th: ["align", "width", ...GENERIC_ALLOWED_ATTRIBUTES],
        a: ["href", "target", ...GENERIC_ALLOWED_ATTRIBUTES],
        ol: ["start", ...GENERIC_ALLOWED_ATTRIBUTES],
        ul: ["start", ...GENERIC_ALLOWED_ATTRIBUTES],
      }
    ),
  });
}
var CustomRenderer = class extends Renderer {
  table(header, body) {
    return `<table width="100%">
<thead>
${header}</thead>
<tbody>
${body}</tbody>
</table>`;
  }
  link(href, title, text) {
    if (!title) {
      return `<a href="${href}" target="_blank">${text}</a>`;
    }
    return `<a href="${href}" title="${title}" target="_blank">${text}</a>`;
  }
};
function renderMarkdownString(str) {
  const html = marked.parse(str, {
    async: false,
    breaks: true,
    gfm: true,
    pedantic: false,
    silent: false,
    renderer: new CustomRenderer(),
  });
  if (typeof html !== "string") {
    throw new Error("marked.parse did not return a string");
  }
  return sanitizer(html);
}
function EmailMarkdown(_a) {
  var _b = _a,
    { markdown } = _b,
    props = __objRest(_b, ["markdown"]);
  const data = useMemo(() => renderMarkdownString(markdown), [markdown]);
  return /* @__PURE__ */ React.createElement(
    "div",
    __spreadProps(__spreadValues({}, props), {
      dangerouslySetInnerHTML: { __html: data },
    })
  );
}

// src/index.tsx
var FONT_FAMILY_SCHEMA = z
  .enum([
    "MODERN_SANS",
    "BOOK_SANS",
    "ORGANIC_SANS",
    "GEOMETRIC_SANS",
    "HEAVY_SANS",
    "ROUNDED_SANS",
    "MODERN_SERIF",
    "BOOK_SERIF",
    "MONOSPACE",
    "ARIAL",
    "ARIAL_BLACK",
    "ARIAL_NARROW",
    "ARIAL_ROUNDED_MT_BOLD",
    "AVANT_GARDE",
    "CALIBRI",
    "CANDARA",
    "CENTURY_GOTHIC",
    "FRANKLIN_GOTHIC_MEDIUM",
    "FUTURA",
    "GENEVA",
    "GILL_SANS",
    "HELVETICA",
    "IMPACT",
    "LUCIDA_GRANDE",
    "OPTIMA",
    "SEGOE_UI",
    "TAHOMA",
    "TREBUCHET_MS",
    "VERDANA",
    "BIG_CASLON",
    "BODONI_MT",
    "BOOK_ANTIQUA",
    "CALISTO_MT",
    "CAMBRIA",
    "DIDOT",
    "GARAMOND",
    "GEORGIA",
    "GOUDY_OLD_STYLE",
    "HOEFLER_TEXT",
    "LUCIDA_BRIGHT",
    "PALATINO",
    "PERPETUA",
    "ROCKWELL",
    "ROCKWELL_EXTRA_BOLD",
    "BASKERVILLE",
    "TIMES_NEW_ROMAN",
    "CONSOLAS",
    "COURIER_NEW",
    "LUCIDA_CONSOLE",
    "LUCIDA_SANS_TYPEWRITER",
    "MONACO",
    "ANDALE_MONO",
    "COPPERPLATE",
    "PAPYRUS",
    "BRUSH_SCRIPT_MT",
  ])
  .nullable()
  .optional();
function getFontFamily(fontFamily) {
  switch (fontFamily) {
    case "MODERN_SANS":
      return '"Helvetica Neue", "Arial Nova", "Nimbus Sans", Arial, sans-serif';
    case "BOOK_SANS":
      return 'Optima, Candara, "Noto Sans", source-sans-pro, sans-serif';
    case "ORGANIC_SANS":
      return 'Seravek, "Gill Sans Nova", Ubuntu, Calibri, "DejaVu Sans", source-sans-pro, sans-serif';
    case "GEOMETRIC_SANS":
      return 'Avenir, "Avenir Next LT Pro", Montserrat, Corbel, "URW Gothic", source-sans-pro, sans-serif';
    case "HEAVY_SANS":
      return 'Bahnschrift, "DIN Alternate", "Franklin Gothic Medium", "Nimbus Sans Narrow", sans-serif-condensed, sans-serif';
    case "ROUNDED_SANS":
      return 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold", Calibri, source-sans-pro, sans-serif';
    case "MODERN_SERIF":
      return 'Charter, "Bitstream Charter", "Sitka Text", Cambria, serif';
    case "BOOK_SERIF":
      return '"Iowan Old Style", "Palatino Linotype", "URW Palladio L", P052, serif';
    case "MONOSPACE":
      return '"Nimbus Mono PS", "Courier New", "Cutive Mono", monospace';
    case "ARIAL":
      return 'Arial, "Helvetica Neue", Helvetica, sans-serif';
    case "ARIAL_BLACK":
      return '"Arial Black", Gadget, sans-serif';
    case "ARIAL_NARROW":
      return '"Arial Narrow", sans-serif';
    case "ARIAL_ROUNDED_MT_BOLD":
      return '"Arial Rounded MT Bold", sans-serif';
    case "AVANT_GARDE":
      return '"Avant Garde", Avantgarde, "Century Gothic", sans-serif';
    case "CALIBRI":
      return 'Calibri, "Trebuchet MS", sans-serif';
    case "CANDARA":
      return 'Candara, "Trebuchet MS", sans-serif';
    case "CENTURY_GOTHIC":
      return '"Century Gothic", CenturyGothic, AppleGothic, sans-serif';
    case "FRANKLIN_GOTHIC_MEDIUM":
      return '"Franklin Gothic Medium", "Franklin Gothic", "ITC Franklin Gothic", sans-serif';
    case "FUTURA":
      return 'Futura, "Trebuchet MS", Arial, sans-serif';
    case "GENEVA":
      return "Geneva, Tahoma, Verdana, sans-serif";
    case "GILL_SANS":
      return '"Gill Sans", "Gill Sans MT", Calibri, sans-serif';
    case "HELVETICA":
      return "Helvetica, Arial, sans-serif";
    case "IMPACT":
      return 'Impact, Haettenschweiler, "Franklin Gothic Bold", Charcoal, "Helvetica Inserat", "Bitstream Vera Sans Bold", sans-serif';
    case "LUCIDA_GRANDE":
      return '"Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva, Verdana, sans-serif';
    case "OPTIMA":
      return 'Optima, Segoe, "Segoe UI", Candara, Calibri, Arial, sans-serif';
    case "SEGOE_UI":
      return '"Segoe UI", Frutiger, "Frutiger Linotype", "Dejavu Sans", "Helvetica Neue", Arial, sans-serif';
    case "TAHOMA":
      return "Tahoma, Verdana, Segoe, sans-serif";
    case "TREBUCHET_MS":
      return '"Trebuchet MS", "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", sans-serif';
    case "VERDANA":
      return "Verdana, Geneva, sans-serif";
    case "BIG_CASLON":
      return '"Big Caslon", "Book Antiqua", "Palatino Linotype", Georgia, serif';
    case "BODONI_MT":
      return '"Bodoni MT", Didot, "Didot LT STD", "Hoefler Text", Garamond, "Times New Roman", serif';
    case "BOOK_ANTIQUA":
      return '"Book Antiqua", Palatino, "Palatino Linotype", "Palatino LT STD", Georgia, serif';
    case "CALISTO_MT":
      return '"Calisto MT", "Bookman Old Style", Bookman, "Goudy Old Style", Garamond, "Hoefler Text", "Bitstream Charter", Georgia, serif';
    case "CAMBRIA":
      return "Cambria, Georgia, serif";
    case "DIDOT":
      return 'Didot, "Didot LT STD", "Hoefler Text", Garamond, "Times New Roman", serif';
    case "GARAMOND":
      return 'Garamond, Baskerville, "Baskerville Old Face", "Hoefler Text", "Times New Roman", serif';
    case "GEORGIA":
      return 'Georgia, Times, "Times New Roman", serif';
    case "GOUDY_OLD_STYLE":
      return '"Goudy Old Style", Garamond, "Big Caslon", "Times New Roman", serif';
    case "HOEFLER_TEXT":
      return '"Hoefler Text", "Baskerville Old Face", Garamond, "Times New Roman", serif';
    case "LUCIDA_BRIGHT":
      return '"Lucida Bright", Georgia, serif';
    case "PALATINO":
      return 'Palatino, "Palatino Linotype", "Palatino LT STD", "Book Antiqua", Georgia, serif';
    case "PERPETUA":
      return 'Perpetua, Baskerville, "Big Caslon", "Palatino Linotype", Palatino, serif';
    case "ROCKWELL":
      return 'Rockwell, "Courier Bold", Courier, Georgia, Times, "Times New Roman", serif';
    case "ROCKWELL_EXTRA_BOLD":
      return '"Rockwell Extra Bold", "Rockwell Bold", monospace';
    case "BASKERVILLE":
      return 'Baskerville, "Baskerville Old Face", "Hoefler Text", Garamond, "Times New Roman", serif';
    case "TIMES_NEW_ROMAN":
      return '"Times New Roman", Times, serif';
    case "CONSOLAS":
      return "Consolas, monaco, monospace";
    case "COURIER_NEW":
      return '"Courier New", Courier, monospace';
    case "LUCIDA_CONSOLE":
      return '"Lucida Console", "Lucida Sans Typewriter", monaco, "Bitstream Vera Sans Mono", monospace';
    case "LUCIDA_SANS_TYPEWRITER":
      return '"Lucida Sans Typewriter", "Lucida Console", monaco, "Bitstream Vera Sans Mono", monospace';
    case "MONACO":
      return 'monaco, Consolas, "Lucida Console", monospace';
    case "ANDALE_MONO":
      return '"Andale Mono", AndaleMono, monospace';
    case "COPPERPLATE":
      return 'Copperplate, "Copperplate Gothic Light", fantasy';
    case "PAPYRUS":
      return "Papyrus, fantasy";
    case "BRUSH_SCRIPT_MT":
      return '"Brush Script MT", cursive';
  }
  return void 0;
}
var COLOR_SCHEMA = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .nullable()
  .optional();
var PADDING_SCHEMA = z
  .object({
    top: z.number(),
    bottom: z.number(),
    right: z.number(),
    left: z.number(),
  })
  .optional()
  .nullable();
var getPadding = (padding) =>
  padding
    ? `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`
    : void 0;
var TextPropsSchema = z.object({
  style: z
    .object({
      color: COLOR_SCHEMA,
      backgroundColor: COLOR_SCHEMA,
      fontSize: z.number().gte(0).optional().nullable(),
      fontFamily: FONT_FAMILY_SCHEMA,
      fontWeight: z.enum(["bold", "normal"]).optional().nullable(),
      textAlign: z.enum(["left", "center", "right"]).optional().nullable(),
      padding: PADDING_SCHEMA,
    })
    .optional()
    .nullable(),
  props: z
    .object({
      markdown: z.boolean().optional().nullable(),
      text: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
});
var TextPropsDefaults = {
  text: "",
};
function Text({ style, props }) {
  var _a, _b, _c, _d, _e, _f;
  const wStyle = {
    color: (_a = style == null ? void 0 : style.color) != null ? _a : void 0,
    backgroundColor:
      (_b = style == null ? void 0 : style.backgroundColor) != null
        ? _b
        : void 0,
    fontSize:
      (_c = style == null ? void 0 : style.fontSize) != null ? _c : void 0,
    fontFamily: getFontFamily(style == null ? void 0 : style.fontFamily),
    fontWeight:
      (_d = style == null ? void 0 : style.fontWeight) != null ? _d : void 0,
    textAlign:
      (_e = style == null ? void 0 : style.textAlign) != null ? _e : void 0,
    padding: getPadding(style == null ? void 0 : style.padding),
  };
  const text =
    (_f = props == null ? void 0 : props.text) != null
      ? _f
      : TextPropsDefaults.text;
  if (props == null ? void 0 : props.markdown) {
    return /* @__PURE__ */ React2.createElement(EmailMarkdown, {
      style: wStyle,
      markdown: text,
    });
  }
  return /* @__PURE__ */ React2.createElement("div", { style: wStyle }, text);
}
export { Text, TextPropsDefaults, TextPropsSchema };
